import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { AdminStatsResponse } from "@family/shared";

function formatNumber(v: number): string {
  return v.toLocaleString("en-US");
}

function formatTokens(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

export function StatsPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const statsQuery = useQuery<AdminStatsResponse>({
    queryKey: ["stats", slug],
    queryFn: () => apiFetch<AdminStatsResponse>(`/families/${slug}/stats`),
    enabled: Boolean(slug),
    refetchInterval: 60_000,
  });

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !can(primary.role, "stats:view")) {
    return <Navigate to="/" replace />;
  }

  const stats = statsQuery.data;

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>Stats</h1>
          <span className="text-subtle" style={{ fontSize: "0.85rem" }}>
            auto-refresh · every 60s
          </span>
        </div>

        {statsQuery.isLoading && <p className="text-muted">Loading…</p>}
        {statsQuery.error && (
          <p className="text-error">
            Error: {(statsQuery.error as Error).message}
          </p>
        )}

        {stats && (
          <>
            <section className="stack">
              <h2>Members</h2>
              <TileGrid>
                <Tile label="Members" value={formatNumber(stats.totals.members)} />
                <Tile label="Pending" value={formatNumber(stats.totals.pending)} />
                <Tile label="Denied" value={formatNumber(stats.totals.denied)} />
                <Tile
                  label="Active today"
                  value={formatNumber(stats.activity.activeToday)}
                  hint="visited in the last 24h"
                />
                <Tile
                  label="Active this week"
                  value={formatNumber(stats.activity.activeThisWeek)}
                />
                <Tile
                  label="Active this month"
                  value={formatNumber(stats.activity.activeThisMonth)}
                />
              </TileGrid>
            </section>

            <section className="stack">
              <h2>Content</h2>
              <TileGrid>
                <Tile label="Posts" value={formatNumber(stats.totals.posts)} />
                <Tile label="Photos" value={formatNumber(stats.totals.photos)} />
                <Tile label="Videos" value={formatNumber(stats.totals.videos)} />
                <Tile label="Comments" value={formatNumber(stats.totals.comments)} />
                <Tile label="Hearts" value={formatNumber(stats.totals.reactions)} />
                <Tile
                  label="Events"
                  value={formatNumber(stats.totals.events)}
                  hint={`${stats.totals.upcomingEvents} upcoming`}
                />
                <Tile
                  label="Open issues"
                  value={formatNumber(stats.totals.openIssues)}
                />
              </TileGrid>
            </section>

            <section className="stack">
              <h2>Last 30 days</h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 12,
                }}
              >
                <SparkCard
                  title="Sign-ups"
                  days={stats.series.days}
                  values={stats.series.signups}
                />
                <SparkCard
                  title="Uploads"
                  days={stats.series.days}
                  values={stats.series.uploads}
                />
                <SparkCard
                  title="Posts"
                  days={stats.series.days}
                  values={stats.series.posts}
                />
              </div>
            </section>

            <section className="stack">
              <h2>AI usage (30 days)</h2>
              <TileGrid>
                <Tile
                  label="Tokens"
                  value={formatTokens(stats.ai.tokens30d)}
                  hint="prompt + response"
                />
                <Tile label="Chat turns" value={formatNumber(stats.ai.turns30d)} />
                <Tile label="API calls" value={formatNumber(stats.ai.calls30d)} />
              </TileGrid>
            </section>

            <section className="stack">
              <h2>
                Quiet members{" "}
                <span className="badge badge--admin" style={{ marginLeft: 8 }}>
                  {stats.activity.quietMembers.length}
                </span>
              </h2>
              <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                Members who haven't posted, uploaded, or commented yet — might
                appreciate a nudge.
              </p>
              {stats.activity.quietMembers.length === 0 ? (
                <p className="text-muted">Everyone has contributed 🎉</p>
              ) : (
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 0,
                    listStyle: "none",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  {stats.activity.quietMembers.map((m) => (
                    <li
                      key={m.userId}
                      style={{
                        border: "1px solid var(--c-border)",
                        borderRadius: "var(--r-pill, 999px)",
                        padding: "4px 12px",
                        fontSize: "0.85rem",
                      }}
                    >
                      {m.name}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </>
  );
}

function TileGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--c-border)",
        borderRadius: 8,
        padding: "12px 14px",
        background: "var(--c-bg-alt)",
      }}
    >
      <div
        style={{
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--c-text-muted)",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.3 }}>
        {value}
      </div>
      {hint && (
        <div className="text-subtle" style={{ fontSize: "0.75rem" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** Minimal 30-day bar chart — plain SVG, no chart library. */
function SparkCard({
  title,
  days,
  values,
}: {
  title: string;
  days: string[];
  values: number[];
}) {
  const width = 240;
  const height = 56;
  const max = Math.max(...values, 1);
  const total = values.reduce((a, b) => a + b, 0);
  const barW = width / Math.max(values.length, 1);

  return (
    <div
      style={{
        border: "1px solid var(--c-border)",
        borderRadius: 8,
        padding: "12px 14px",
        background: "var(--c-bg-alt)",
      }}
    >
      <div className="row-between" style={{ marginBottom: 6 }}>
        <span
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "var(--c-text-muted)",
          }}
        >
          {title}
        </span>
        <span style={{ fontWeight: 700 }}>{formatNumber(total)}</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={`${title} per day over the last ${values.length} days`}
      >
        {values.map((v, i) => {
          const h = v === 0 ? 1.5 : Math.max((v / max) * (height - 4), 3);
          return (
            <rect
              key={days[i] ?? i}
              x={i * barW + 1}
              y={height - h}
              width={Math.max(barW - 2, 1.5)}
              height={h}
              rx={1}
              fill={v === 0 ? "var(--c-border)" : "var(--c-accent)"}
            >
              <title>{`${days[i]}: ${v}`}</title>
            </rect>
          );
        })}
      </svg>
      <div
        className="text-subtle"
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.7rem",
          marginTop: 4,
        }}
      >
        <span>{days[0]}</span>
        <span>today</span>
      </div>
    </div>
  );
}
